import React, {Fragment, useReducer, useEffect, useCallback} from "react";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import CreatableSelect from "react-select/creatable";
import DatePicker from "react-date-picker";
import AsyncSelect from "react-select/async";
import { EditorState, ContentState, convertFromHTML } from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import xor from 'lodash/xor';
import isEqual from 'lodash.isequal';

import { headerText, footerText } from "./default-header-footer-content";
import TextInput from "../text-input";
import TextAreaInput from "../textarea-input";
import {LoadingButton} from "../shared/loading-button";
import ai from "../../helpers/axios";
import getApiErrorMessage from "../../helpers/get-api-error-message";
import timeFormatter from "../../helpers/timeFormatter";
import {
  convertLocalToUTCDate,
  convertUTCToLocalDate,
} from "../../helpers/dates";
import PageHeader from "../shared/page-header";
import UnsavedChangesWarningModal from "../unsaved-changes-warning-modal/unsaved-changes-warning-modal";

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const areArraysEqual = (arr1 , arr2 ) => {
  const arr1values = arr1?.map(({value}) => value) ?? [];
  const arr2values = arr2?.map(({value}) => value) ?? [];

  return xor(arr1values, arr2values).length === 0;
};

const copyObjectsArray = array => structuredClone(array);

const reducer = (state, action) => {
  switch (action.type) {
    case "dataBeganDownloading": {
      return {
        ...state,
        loading: true,
      };
    }
    case "leadDataDownloaded": {
      const {firstName, lastName} = action.payload.data ?? {};

      return {
        ...state,
        lead: {
          firstName,
          lastName,
        }
      };
    }
    case "quoteDataDownloaded": {
      const {
        firstName,
        lastName,
        email,
        legs,
        aircraftOptions,
        subject,
        cc,
        bcc,
        bccSender,
        organizationId,
        headerContent,
        footerContent,
        leadId,
        leadData,
      } = action.payload.data;

      const headerTextHTML = convertFromHTML(headerContent);
      const headerTextState = ContentState.createFromBlockArray(
        headerTextHTML.contentBlocks,
        headerTextHTML.entityMap,
      );

      const footerTextHTML = convertFromHTML(footerContent);
      const footerTextState = ContentState.createFromBlockArray(
        footerTextHTML.contentBlocks,
        footerTextHTML.entityMap,
      );

      const prospectsFullName = [leadData?.firstName, leadData?.lastName].filter(Boolean).join(' ');

      const initialValues = {
        firstName,
        lastName,
        email: email.map((item) => ({ label: item, value: item })),
        legs: legs.map((leg) => ({
          ...leg,
          departureDate: new Date(leg.departureDate),
        })),
        aircraftOptions: aircraftOptions.map((option) => {
          option.fuelStops = option.fuelStops?.filter(Boolean)?.length ? option.fuelStops : [""];

          if (option.ownerApprovalRequired) {
            return {
              ...option,
              importantNotesList: [
                ...(option.importantNotesList || []),
                "Owner approval",
              ].filter((item, i, arr) => arr.indexOf(item) === i), // Remove duplicates
            };
          }
          return option;
        }),
        subject,
        cc: cc.map((item) => ({ label: item, value: item })),
        bcc: bcc.map((item) => ({ label: item, value: item })),
        bccSender,
        organizationId,
        headerEditor: EditorState.createWithContent(headerTextState),
        footerEditor: EditorState.createWithContent(footerTextState),
        leadId,
      };

      return {
        ...state,
        prospectsFullName,
        ...initialValues,
        initialFormValues: initialValues
      };
    }
    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }
    case "legAdded": {
      let newAircraftOptions = copyObjectsArray(state.aircraftOptions);

      newAircraftOptions = newAircraftOptions.map((item) => {
        item.legDurations.push("");
        item.fuelStops.push("");

        return item;
      });

      return {
        ...state,
        legs: [
          ...state.legs,
          {
            departureDate: "",
            departureAirport: {
              airportName: "",
              id: "",
              code: "",
              city: "",
              localCode: "",
            },
            arrivalAirport: {
              airportName: "",
              id: "",
              code: "",
              city: "",
              localCode: "",
            },
            passengers: "",
          },
        ],
        aircraftOptions: newAircraftOptions,
        legsChanged: true,
      };
    }
    case "legDeleted": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray.splice(action.index, 1);

      let aircraftOptions = [...state.aircraftOptions];

      aircraftOptions = aircraftOptions.map((item) => {
        item.legDurations.splice(action.index, 1);
        item.fuelStops.splice(action.index, 1);

        return item;
      });

      return {
        ...state,
        legs: newLegsArray,
        aircraftOptions,
        legsChanged: true,
      };
    }
    case "legFieldChanged": {
      let newLegsArray = copyObjectsArray(state.legs);
      newLegsArray[action.payload.legIndex][action.payload.field] =
        action.payload.value;
      return {
        ...state,
        legs: newLegsArray,
        legsChanged: true,
      };
    }
    case "optionAdded": {
      return {
        ...state,
        aircraftOptions: [
          ...state.aircraftOptions,
          {
            aircraft: { id: "", yom: "", name: "", seats: "", opi: "" },
            totalCost: "",
            interiorRefurbYear: "",
            notes: "",
            legDurations: [""],
            fuelStops: [""],
            safetyStandards: [],
            amenities: [],
            ownerApprovalRequired: false,
            importantNotesList: [], // New array for important notes checkboxes
          },
        ],
        aircraftOptionsChanged: true,
      };
    }
    case "optionFieldChanged": {
      let newOptionsArray = copyObjectsArray(state.aircraftOptions);
      newOptionsArray[action.payload.optionIndex][action.payload.field] =
        action.payload.value;
      return {
        ...state,
        aircraftOptions: newOptionsArray,
        aircraftOptionsChanged: true,
      };
    }
    case "flightTimesDownloaded": {
      let { index, values } = action;

      let newOptionsArray = copyObjectsArray(state.aircraftOptions);

      let newLegDurationsArray = [
        ...newOptionsArray[action.index].legDurations,
      ];

      values.forEach((item) => {
        if (item.status === "rejected" && isNaN(item?.value?.legIndex)) {
          toast.error(
            `Flight times not available for Leg ${
              item?.value?.legIndex + 1
            }, please input manually.`,
            toastOptions,
          );
          return;
        } else if (item.status === "rejected") {
          toast.error(
            `Flight times not available - please input manually.`,
            toastOptions,
          );
          return;
        }

        if (
          !item?.value?.timeWithWindAdjustment ||
          !item?.value?.status ||
          isNaN(item?.value?.legIndex)
        ) {
          toast.error(
            "Flight times not available - please input manually.",
            toastOptions,
          );
          return;
        }

        newLegDurationsArray[item.value.legIndex] = timeFormatter(
          item.value.timeWithWindAdjustment,
        );

        newOptionsArray[index].legDurations = newLegDurationsArray;

        toast.success(
          "Flight Times were populated successfully.",
          toastOptions,
        );

        return newOptionsArray;
      });

      return {
        ...state,
        aircraftOptions: newOptionsArray,
        aircraftOptionsChanged: true,
      };
    }
    case "optionCheckboxFieldChanged": {
      let newOptionsArray = copyObjectsArray(state.aircraftOptions);

      let newCheckboxList = [
        ...newOptionsArray[action.payload.optionIndex][action.payload.field],
      ];

      newCheckboxList = new Set(newCheckboxList);

      if (action.payload.value) {
        newCheckboxList.add(action.payload.name);
      } else {
        newCheckboxList.delete(action.payload.name);
      }

      newOptionsArray[action.payload.optionIndex][action.payload.field] = [
        ...newCheckboxList,
      ];

      return {
        ...state,
        aircraftOptions: newOptionsArray,
        aircraftOptionsChanged: true,
      };
    }
    case "optionAircraftSubFieldChanged": {
      let newOptionsArray = copyObjectsArray(state.aircraftOptions);
      newOptionsArray[action.payload.optionIndex].aircraft[
        action.payload.field
        ] = action.payload.value;
      return {
        ...state,
        aircraftOptions: newOptionsArray,
        aircraftOptionsChanged: true,
      };
    }
    case "optionDeleted": {
      let newOptionsArray = copyObjectsArray(state.aircraftOptions);
      newOptionsArray.splice(action.index, 1);

      return {
        ...state,
        aircraftOptions: newOptionsArray,
        aircraftOptionsChanged: true,
      };
    }
    case "optionArrayFieldChanged": {
      let newOptionsArray = copyObjectsArray(state.aircraftOptions);

      let newLegDurationsArray = [
        ...newOptionsArray[action.payload.optionIndex].legDurations,
      ];

      let newFuelStopsArray = [
        ...newOptionsArray[action.payload.optionIndex].fuelStops,
      ];

      if (action.payload.arrayField === "legDuration") {
        newLegDurationsArray[action.payload.legIndex] = action.payload.value;
        newOptionsArray[action.payload.optionIndex].legDurations =
          newLegDurationsArray;
      } else if (action.payload.arrayField === "fuelStop") {
        newFuelStopsArray[action.payload.legIndex] = action.payload.value;
        newOptionsArray[action.payload.optionIndex].fuelStops =
          newFuelStopsArray;
      }

      return {
        ...state,
        aircraftOptions: newOptionsArray,
        aircraftOptionsChanged: true,
      };
    }

    case "unsavedChanges": {
      const updatedUnsavedChanges = {...state.unsavedChanges};

      if (!action.value) {
        delete updatedUnsavedChanges[action.field];
      } else {
        updatedUnsavedChanges[action.field] = true;
      }

      return {
        ...state,
        unsavedChanges: updatedUnsavedChanges
      };
    }
    default:
      break;
  }
  return state;
};

const headerTextHTML = convertFromHTML(headerText);
const headerTextState = ContentState.createFromBlockArray(
  headerTextHTML.contentBlocks,
  headerTextHTML.entityMap,
);

const footerTextHTML = convertFromHTML(footerText);
const footerTextState = ContentState.createFromBlockArray(
  footerTextHTML.contentBlocks,
  footerTextHTML.entityMap,
);

const DuplicateQuoteContent = () => {
  const { quoteId, aircraftOptionId, leadId } = useParams();
  const history = useHistory();
  const location = useLocation();

  let sourcingRecords = [];
  let leadL2Data = null;

  if (
    location?.state?.sourcingRecords &&
    location?.state?.sourcingRecords?.length
  ) {
    sourcingRecords = location.state.sourcingRecords;
  }

  if (location?.state?.leadL2Data) {
    leadL2Data = location?.state?.leadL2Data;
  }

  const initialValues = {
    firstName: "",
    lastName: "",
    email: [],
    legs: [
      {
        departureDate: "",
        departureAirport: {
          airportName: "",
          id: "",
          code: "",
          city: "",
          localCode: "",
        },
        arrivalAirport: {
          airportName: "",
          id: "",
          code: "",
          city: "",
          localCode: "",
        },
        passengers: "",
      },
    ],
    aircraftOptions: [
      {
        aircraft: { id: "", yom: "", name: "", seats: "", opi: "" },
        totalCost: "",
        interiorRefurbYear: "",
        notes: "",
        fuelStops: [""],
        legDurations: [""],
        safetyStandards: [],
        amenities: [],
        ownerApprovalRequired: false,
        importantNotesList: [],
      },
    ],
    subject: "",
    cc: [],
    bcc: [],
    bccSender: true,
    headerEditor: EditorState.createWithContent(headerTextState),
    footerEditor: EditorState.createWithContent(footerTextState),
  };

  const initialState = {
    loading: true,
    sourcingRecordsProcessed: false,
    emailInputValue: "",
    ccInputValue: "",
    bccInputValue: "",

    isFormLoading: false,
    lead: {},
    prospectsFullName: "",

    ...initialValues,

    legsChanged: false,
    aircraftOptionsChanged: false,
    initialFormValues: initialValues,
    unsavedChanges: {}
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const setUnsavedChanges = useCallback(({field, value}) => {
    dispatch({
      type: "unsavedChanges",
      field,
      value
    })
  }, []);

  const { getAccessTokenSilently, loginWithRedirect, user } = useAuth0();

  useEffect(() => {
    if (document) {
      document.getElementById("page-wrap").scrollIntoView({ behavior: "smooth" });
    }

    processSourcingRecords();
  }, [state.loading]);

  let aircraftTypeSortOrder = {
    "Executive Turboprop": 1,
    "Compact Light Jet": 2,
    "Light Jet": 3,
    "Midsize Jet": 4,
    "Super Midsize Jet": 5,
    "Heavy Jet": 6,
    "Ultra Long Range Jet": 7,
    "Cargo Airliner": 8,
    "Turboprop Airliner": 9,
    "Regional Airliner": 10,
    "Business Airliner": 11,
  };

  const processSourcingRecords = async () => {
    if (!sourcingRecords || sourcingRecords?.length === 0 || !leadL2Data) {
      return;
    }

    let completeSourcingRecords = sourcingRecords.map((record) => {
      let matchingAircraftData = leadL2Data?.sourcingAircraftData.filter(
        (sourcingAircraft) => {
          return sourcingAircraft?._id === record?.aircraft?.id;
        },
      );

      let matchingOperatorData = leadL2Data?.operatorData.filter((operator) => {
        return operator?._id === record?.operator?.id;
      });

      if (matchingAircraftData?.length) {
        matchingAircraftData = matchingAircraftData[0];
      } else {
        matchingAircraftData = {};
      }

      if (matchingOperatorData?.length) {
        matchingOperatorData = matchingOperatorData[0];
      } else {
        matchingOperatorData = {};
      }

      record.fullAircraftData = matchingAircraftData;
      record.fullOperatorData = matchingOperatorData;

      return record;
    });

    let completeSourcingRecordsSorted = completeSourcingRecords.sort((a, b) => {
      if (a.totalClientPayment === b.totalClientPayment) {
        return (
          aircraftTypeSortOrder[a.fullAircraftData.type] -
          aircraftTypeSortOrder[b.fullAircraftData.type]
        );
      }

      return a.totalClientPayment - b.totalClientPayment;
    });

    let aircraftOptionsData = completeSourcingRecordsSorted.map((record) => {
      let totalCost = "";
      if (record?.totalClientPayment) {
        totalCost = record?.totalClientPayment.toFixed(2);
      }

      let yom,
        interiorRefurbYear,
        notes,
        opi = "";

      if (record?.yom) {
        yom = `${record.yom}`;

        if (record?.yomIsRange && record?.yomEnd) {
          yom = `${record.yom} - ${record.yomEnd}`;
        }
      } else {
        yom = `${record.fullAircraftData?.yom}`;
      }

      if (record?.interiorRefurbYear) {
        interiorRefurbYear = `${record.interiorRefurbYear}`;

        if (
          record?.interiorRefurbYearIsRange &&
          record?.interiorRefurbYearEnd
        ) {
          interiorRefurbYear = `${record.interiorRefurbYear} - ${record.interiorRefurbYearEnd}`;
        }
      }

      if (
        record?.includeOPIOnQuote &&
        record?.fullOperatorData?.composedScore
      ) {
        opi = record?.fullOperatorData?.composedScore;
      }

      if (record?.includeNotesOnQuote && record?.notes) {
        notes = record.notes;
      }

      let amenities = [];
      let safetyStandards = [];

      if (record?.amenities?.length) {
        amenities = record.amenities.map((amenity) => {
          if (amenity === "WiFi Billed") {
            return "WiFi (adtl charges may apply)";
          }

          return amenity;
        });
      }

      if (record?.safetyStandards?.length) {
        safetyStandards = record.safetyStandards;
      }

      let option = {
        aircraft: {
          name: record?.aircraft?.name || "",
          seats:
            record?.aircraft?.seats || record?.fullAircraftData?.seats || "",
          yom,
          opi,
          id: record?.aircraft?.id,
        },
        totalCost: totalCost || 0,
        interiorRefurbYear,
        notes,
        ownerApprovalRequired: record?.ownerApproval ? true : false,
        fuelStops: [""],
        legDurations: [""],
        safetyStandards: safetyStandards,
        amenities: amenities,
        sourcingRecordId: record?._id,
      };

      return option;
    });

    dispatch({
      type: "fieldChanged",
      field: "aircraftOptions",
      value: aircraftOptionsData,
    });

    dispatch({
      type: "fieldChanged",
      field: "sourcingRecordsProcessed",
      value: true,
    });
  };

  const getLeadData = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const {data} = await ai.auth(token).get(`/api/leads/${leadId}`);

    dispatch({
      type: "leadDataDownloaded",
      payload: {
        data
      },
    });
  };

  const getQuoteData = async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const { data } = await ai.auth(token).get(`/api/quotes/${quoteId}`);

    dispatch({
      type: "quoteDataDownloaded",
      payload: {
        data: {
          ...data,
          aircraftOptions: data.aircraftOptions?.filter(aircraftOption => aircraftOption._id === aircraftOptionId),
        },
      },
    });
  };

  useEffect(() => {
    getQuoteData();
    if (leadId) {
      getLeadData();
    }

    document.title = "Quote Builder | evoJets HQ";
  }, [leadId]);

  const validateEmail = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  const handleTextInputChange = (e) => {
    setUnsavedChanges({
      field: e.target.name,
      value: e.target.value !== state.initialFormValues[e.target.name],
    });

    return dispatch({
      type: "fieldChanged",
      field: e.target.name,
      value: e.target.value,
    });
  };

  const handleEmailChange = (newValue, actionMeta) => {
    setUnsavedChanges({
      field: 'email',
      value: !areArraysEqual(newValue, state.initialFormValues.email),
    });

    dispatch({
      type: "fieldChanged",
      field: "email",
      value: newValue || [],
    });
  };

  const handleCcChange = (newValue, actionMeta) => {
    setUnsavedChanges({
      field: 'cc',
      value: !areArraysEqual(newValue, state.initialFormValues.cc),
    });

    dispatch({
      type: "fieldChanged",
      field: "cc",
      value: newValue || [],
    });
  };

  const handleBccChange = (newValue, actionMeta) => {
    setUnsavedChanges({
      field: 'bcc',
      value: !areArraysEqual(newValue, state.initialFormValues.bcc),
    });

    dispatch({
      type: "fieldChanged",
      field: "bcc",
      value: newValue || [],
    });
  };

  const handleEmailInputChange = (inputValue, action) => {
    setUnsavedChanges({
      field: 'emailInputValue',
      value: !!inputValue,
    });

    dispatch({
      type: "fieldChanged",
      field: "emailInputValue",
      value: inputValue,
    });

    if (
      action.action === "menu-close" &&
      validateEmail(state.emailInputValue)
    ) {
      const newEmails = [...state.email, createOption(state.emailInputValue)];

      setUnsavedChanges({
        field: 'email',
        value: !areArraysEqual(newEmails, state.initialFormValues.email),
      });

      setUnsavedChanges({
        field: 'emailInputValue',
        value: false,
      });

      dispatch({
        type: "fieldChanged",
        field: "email",
        value: newEmails,
      });
      dispatch({
        type: "fieldChanged",
        field: "emailInputValue",
        value: "",
      });
    }
  };

  const handleCcInputChange = (inputValue, action) => {
    setUnsavedChanges({
      field: 'ccInputValue',
      value: !!inputValue,
    });

    dispatch({
      type: "fieldChanged",
      field: "ccInputValue",
      value: inputValue,
    });

    if (action.action === "menu-close" && validateEmail(state.ccInputValue)) {
      const newEmails = [...state.cc, createOption(state.ccInputValue)];

      setUnsavedChanges({
        field: 'cc',
        value: !areArraysEqual(newEmails, state.initialFormValues.cc),
      });

      setUnsavedChanges({
        field: 'ccInputValue',
        value: false,
      });

      dispatch({
        type: "fieldChanged",
        field: "cc",
        value: newEmails,
      });
      dispatch({
        type: "fieldChanged",
        field: "ccInputValue",
        value: "",
      });
    }
  };

  const handleBccInputChange = (inputValue, action) => {
    setUnsavedChanges({
      field: 'bccInputValue',
      value: !!inputValue,
    });

    dispatch({
      type: "fieldChanged",
      field: "bccInputValue",
      value: inputValue,
    });

    if (action.action === "menu-close" && validateEmail(state.bccInputValue)) {
      const newEmails = [...state.bcc, createOption(state.bccInputValue)];

      setUnsavedChanges({
        field: 'bcc',
        value: !areArraysEqual(newEmails, state.initialFormValues.bcc),
      });

      setUnsavedChanges({
        field: 'bccInputValue',
        value: false,
      });

      dispatch({
        type: "fieldChanged",
        field: "bcc",
        value: newEmails,
      });
      dispatch({
        type: "fieldChanged",
        field: "bccInputValue",
        value: "",
      });
    }
  };

  const emailComponents = {
    DropdownIndicator: null,
  };

  const createOption = (label) => ({
    label,
    value: label,
  });

  const handleEmailKeyDown = (event) => {
    const { emailInputValue, email } = state;
    if (!emailInputValue || validateEmail(emailInputValue)) return;

    switch (event.key) {
      case "Enter":
      case "Tab":
        const newEmails = [...(state.email ?? []), createOption(state.emailInputValue)];

        setUnsavedChanges({
          field: 'email',
          value: !areArraysEqual(newEmails, state.initialFormValues.email),
        });

        setUnsavedChanges({
          field: 'emailInputValue',
          value: false,
        });

        dispatch({
          type: "fieldChanged",
          field: "email",
          value: newEmails,
        });

        dispatch({
          type: "fieldChanged",
          field: "emailInputValue",
          value: "",
        });

        event.preventDefault();
    }
  };

  const handleCcKeyDown = (event) => {
    const { ccInputValue, cc } = state;
    if (!ccInputValue) return;

    switch (event.key) {
      case "Enter":
      case "Tab":
        const newEmails = [...(state.cc ?? []), createOption(state.ccInputValue)];

        setUnsavedChanges({
          field: 'cc',
          value: !areArraysEqual(newEmails, state.initialFormValues.cc),
        });

        setUnsavedChanges({
          field: 'ccInputValue',
          value: false,
        });

        dispatch({
          type: "fieldChanged",
          field: "cc",
          value: newEmails,
        });

        dispatch({
          type: "fieldChanged",
          field: "ccInputValue",
          value: "",
        });

        event.preventDefault();
    }
  };

  const handleBccKeyDown = (event) => {
    const { bccInputValue, bcc } = state;
    if (!bccInputValue) return;

    switch (event.key) {
      case "Enter":
      case "Tab":
        const newEmails = [...(state.bcc ?? []), createOption(state.bccInputValue)];

        setUnsavedChanges({
          field: 'bcc',
          value: !areArraysEqual(newEmails, state.initialFormValuesbcc),
        });

        setUnsavedChanges({
          field: 'bccInputValue',
          value: false,
        });

        dispatch({
          type: "fieldChanged",
          field: "bcc",
          value: newEmails,
        });

        dispatch({
          type: "fieldChanged",
          field: "bccInputValue",
          value: "",
        });

        event.preventDefault();
    }
  };

  const handleAddLeg = (event) => {
    event.preventDefault();

    dispatch({ type: "legAdded" });
  };

  const handleAddOption = (event) => {
    event.preventDefault();

    dispatch({ type: "optionAdded" });
  };

  const loadAirportOptions = async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const { data } = await ai.auth(token).get(`/api/airports/search?q=${inputValue}`);

    return data.length >= 1 ? data.map((i) => ({
      value: i._id,
      label: i.airportName,
      code: i.code,
      city: i.city,
      localCode: i.localCode,
    })) : [];
  };

  const loadAircraftOptions = async (inputValue) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const { data } = await ai.auth(token).get(`/api/aircrafts/search?q=${inputValue}`, {
      params: {
        query: inputValue,
        page: 1,
        countPerPage: 50,
      },
    });

    return data.aircraft.length >= 1 ? data.aircraft.map((aircraft) => {
      return {
        value: aircraft._id,
        label: aircraft.name,
        id: aircraft._id,
        yom: aircraft.yom,
        name: aircraft.name,
        opi: aircraft.opi,
        seats: aircraft.seats,
        speed: aircraft.cruiseSpeed,
      };
    }) : [];
  };

  const safetyStandards = [
    { label: "ARG/US Gold", value: false },
    { label: "ARG/US Gold+", value: false },
    { label: "ARG/US Platinum", value: false },
    { label: "Wyvern Registered", value: false },
    { label: "Wyvern Wingman", value: false },
  ];

  const amenities = [
    { label: "WiFi - Free", value: false },
    { label: "WiFi - Free (domestic)", value: false },
    { label: "WiFi - Adtl Fees", value: false },
    { label: "Cabin Attendant", value: false },
    { label: "Catering Included", value: false },
  ];

  const importantNotes = [
    { label: "Single pilot", value: false },
    { label: "Lav seat", value: false },
    { label: "No lav", value: false },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (state.isFormLoading) {
      return;
    }

    let {
      bccInputValue,
      ccInputValue,
      emailInputValue,
      email,
      cc,
      bcc,
      headerEditor,
      footerEditor,
      initialFormValues,
      ...restOfData
    } = state;

    let headerContent = stateToHTML(headerEditor.getCurrentContent());
    let footerContent = stateToHTML(footerEditor.getCurrentContent());

    email = email.map(({ value }) => value);
    cc = cc.map(({ value }) => value);
    bcc = bcc.map(({ value }) => value);

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    let quoteData = {
      email,
      bcc,
      cc,
      ...restOfData,
      headerContent,
      footerContent,
      createNewLead: !leadId,
    };

    dispatch({
      type: "fieldChanged",
      field: "isFormLoading",
      value: true,
    })

    let response;

    try {
      response = await ai.auth(token).post(`/api/quotes/duplicate`, quoteData).catch((err) => {
        const message = getApiErrorMessage(err, "");
        if (message) {
          toast.error(message, toastOptions);
        } else {
          alert(
            "Please fill out all required fields: aircraft names for each leg, and aircraft prices for each leg. ",
          );
        }
      });

      if (!response?.data) {
        return;
      }

      dispatch({
        type: "fieldChanged",
        field: "unsavedChanges",
        value: {},
      });
    } finally {
      dispatch({
        type: "fieldChanged",
        field: "isFormLoading",
        value: false,
      })
    }
    setTimeout(() => {
      toast.success("Quote Duplicated Successfully!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }, 50);

    history.push({ pathname: `/quotes/${response.data._id}` });
  };

  const handleFlightTimeCalculation = async (index) => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    const params = {
      legs: state.legs.map((item, index) => {
        return {
          arrivalAirportId: item.arrivalAirport.id,
          departureAirportId: item.departureAirport.id,
          legIndex: index,
        };
      }),
      aircraftId: state.aircraftOptions[index].aircraft.id,
    };

    const response = await ai.auth(token).post(`/api/quotes/flightTimes`, params).catch((err) => {
      console.error(err.response.data);
      toast.error(
        "Please fill out all required fields: ensure an aircraft is selected for this Aircraft Option, and departure and arrival airports for all legs.",
        toastOptions,
      );
      return;
    });

    if (response && response.data) {
      dispatch({
        type: "flightTimesDownloaded",
        values: response.data,
        index: index,
      });
    }
  };

  useEffect(() => {
    if (state.legsChanged) {
      setUnsavedChanges({
        field: 'legs',
        value: !isEqual(state.legs, state.initialFormValues.legs),
      });

      dispatch({
        type: "fieldChanged",
        field: "legsChanged",
        value: false,
      })
    }
  }, [setUnsavedChanges, state.legsChanged, state.legs, state.initialFormValues.legs]);

  useEffect(() => {
    if (state.aircraftOptionsChanged) {
      setUnsavedChanges({
        field: 'aircraftOptions',
        value: !isEqual(state.aircraftOptions, state.initialFormValues.aircraftOptions),
      });

      dispatch({
        type: "fieldChanged",
        field: "aircraftOptionsChanged",
        value: false,
      })
    }
  }, [
    setUnsavedChanges,
    state.aircraftOptionsChanged,
    state.aircraftOptions,
    state.initialFormValues.aircraftOptions
  ]);

  return (
    <Fragment>
      <div className="mb-40">
        <PageHeader
          pageTitle={`Duplicate Quote (${leadId ? "Current Lead" : "New Lead"})`}
          breadcrumbs={[
            {link: '/leads', label: 'Leads'},
            ...(state.prospectsFullName ? [{
              link: `/leads/${state.leadId}`,
              label: `Lead - ${state.prospectsFullName}`
            }] : []),
            {label: `Duplicate Quote (${leadId ? "Current Lead" : "New Lead"})`, isCurrentPage: true},
          ]}
          goBackLink={leadId ? `/leads/${leadId}` : '/leads'}
        />
      </div>

      <form id="newQuote" onSubmit={handleSubmit}>
        <div className="two-fields">
          <TextInput
            name="firstName"
            label="First Name"
            handleChange={handleTextInputChange}
            value={state.firstName}
          />

          <TextInput
            name="lastName"
            label="Last Name"
            handleChange={handleTextInputChange}
            value={state.lastName}
          />
        </div>

        <div className="form-group mt20">
          <label htmlFor="email">Email</label>
          <CreatableSelect
            className="email-select"
            classNamePrefix="select"
            placeholder="Email"
            components={emailComponents}
            isClearable
            isMulti
            menuIsOpen={false}
            onChange={handleEmailChange}
            onInputChange={handleEmailInputChange}
            onKeyDown={handleEmailKeyDown}
            inputValue={state.emailInputValue}
            value={state.email}
            name={"email"}
            inputId={"email"}
          ></CreatableSelect>
        </div>
        <div className="form-group">
          <label htmlFor="cc">CC</label>
          <CreatableSelect
            className="email-select"
            classNamePrefix="select"
            placeholder="Cc"
            components={emailComponents}
            isClearable
            isMulti
            menuIsOpen={false}
            onChange={handleCcChange}
            onInputChange={handleCcInputChange}
            onKeyDown={handleCcKeyDown}
            inputValue={state.ccInputValue}
            value={state.cc}
            inputId={"cc"}
            name={"cc"}
          ></CreatableSelect>
        </div>

        <div className="form-group">
          <label htmlFor="bcc">BCC</label>

          <CreatableSelect
            className="email-select"
            classNamePrefix="select"
            placeholder="Bcc"
            components={emailComponents}
            isClearable
            isMulti
            menuIsOpen={false}
            onChange={handleBccChange}
            onInputChange={handleBccInputChange}
            onKeyDown={handleBccKeyDown}
            inputValue={state.bccInputValue}
            value={state.bcc}
            inputId={"bcc"}
            name={"bcc"}
          ></CreatableSelect>
        </div>

        <input
          name="bccSender"
          type="checkbox"
          checked={state.bccSender ? "checked" : ""}
          onChange={(e) => {
            setUnsavedChanges({
              field: 'bccSender',
              value: e.target.checked !== state.initialFormValues.bccSender,
            });

            dispatch({
              type: "fieldChanged",
              field: "bccSender",
              value: e.target.checked,
            })
          }}
        />
        <label htmlFor="bccSender">Bcc Sender</label>

        <TextInput
          name="subject"
          label="Subject"
          handleChange={handleTextInputChange}
          value={state.subject}
          className={"subject-field"}
        />

        <Editor
          editorState={state.headerEditor}
          wrapperClassName="header-editor-wrapper"
          editorClassName="header-editor"
          onEditorStateChange={(editorState) => {
              setUnsavedChanges({
                field: 'headerEditor',
                value:
                  editorState?.getCurrentContent().getPlainText()
                  !== state.initialFormValues.headerEditor?.getCurrentContent().getPlainText(),
              });

              dispatch({
                type: "fieldChanged",
                field: "headerEditor",
                value: editorState,
              })
            }
          }
        />

        <div className="flight-details-container">
          <h3>Flight Details</h3>

          {state.legs.map((leg, index) => (
            <div className="flight-details-block" key={index}>
              <div className="three-fields">
                <div className="datepickerContainer">
                  <label htmlFor={`departureDate[${index}]`}>
                    Departure Date
                  </label>
                  {/* {JSON.stringify(leg.departureDate)} */}
                  <DatePicker
                    name={`departureDate[${index}]`}
                    onChange={(date) => {
                      dispatch({
                        type: "legFieldChanged",
                        payload: {
                          legIndex: index,
                          field: "departureDate",
                          value: convertLocalToUTCDate(date),
                        },
                      });
                    }}
                    value={convertUTCToLocalDate(leg.departureDate)}
                  />
                </div>

                <div className="airportContainer">
                  <label htmlFor={`departureAirport[${index}]`}>
                    Departure Airport
                  </label>

                  <AsyncSelect
                    key={`departure-${leg.departureAirport.airportName}`}
                    name={`departureAirport[${index}]`}
                    className="departure-airport-select"
                    classNamePrefix="select"
                    cacheOptions
                    loadOptions={loadAirportOptions}
                    placeholder="Search Airports"
                    defaultValue={{
                      label: leg.departureAirport.airportName,
                      value: leg.departureAirport.id,
                    }}
                    onChange={(selectedAirport) => {
                      dispatch({
                        type: "legFieldChanged",
                        payload: {
                          legIndex: index,
                          field: "departureAirport",
                          value: {
                            airportName: selectedAirport?.label || "",
                            id: selectedAirport?.value || "",
                            code: selectedAirport?.code || "",
                            city: selectedAirport?.city || "",
                            localCode: selectedAirport?.localCode || "",
                          },
                        },
                      });
                    }}
                  ></AsyncSelect>
                </div>

                <div className="airportContainer">
                  <label htmlFor={`departureAirport[${index}]`}>
                    Arrival Airport
                  </label>

                  <AsyncSelect
                    key={`arrival-${leg.arrivalAirport.airportName}`}
                    name={`arrivalAirport[${index}]`}
                    className="arrival-airport-select"
                    classNamePrefix="select"
                    cacheOptions
                    loadOptions={loadAirportOptions}
                    placeholder="Search Airports"
                    defaultValue={{
                      label: leg.arrivalAirport.airportName,
                      value: leg.arrivalAirport.id,
                    }}
                    onChange={(selectedAirport) => {
                      dispatch({
                        type: "legFieldChanged",
                        payload: {
                          legIndex: index,
                          field: "arrivalAirport",
                          value: {
                            airportName: selectedAirport?.label || "",
                            id: selectedAirport?.value || "",
                            code: selectedAirport?.code || "",
                            city: selectedAirport?.city || "",
                            localCode: selectedAirport?.localCode || "",
                          },
                        },
                      });
                    }}
                  ></AsyncSelect>
                </div>
              </div>

              <div className="three-fields">
                <TextInput
                  type={"number"}
                  name={`passengers[${index}]`}
                  label="Passengers"
                  handleChange={(e) => {
                    dispatch({
                      type: "legFieldChanged",
                      payload: {
                        legIndex: index,
                        field: "passengers",
                        value: parseInt(e.target.value) || "",
                      },
                    });
                  }}
                  value={leg.passengers}
                />

                <TextInput
                  name={`departureCity[${index}]`}
                  label="Departure City"
                  handleChange={(e) => {
                    dispatch({
                      type: "legFieldChanged",
                      payload: {
                        legIndex: index,
                        field: "departureAirport",
                        value: {
                          ...leg.departureAirport,
                          city: e.target.value,
                        },
                      },
                    });
                  }}
                  value={leg.departureAirport.city}
                />

                <TextInput
                  name={`arrivalCity[${index}]`}
                  label="Arrival City"
                  handleChange={(e) => {
                    dispatch({
                      type: "legFieldChanged",
                      payload: {
                        legIndex: index,
                        field: "arrivalAirport",
                        value: {
                          ...leg.arrivalAirport,
                          city: e.target.value,
                        },
                      },
                    });
                  }}
                  value={leg.arrivalAirport.city}
                />
              </div>

              {index >= 1 && (
                <button
                  className="deleteLeg action-button"
                  onClick={(e) => {
                    e.preventDefault();
                    return dispatch({ type: "legDeleted", index: index });
                  }}
                  type="button"
                >
                  Delete Leg
                </button>
              )}
            </div>
          ))}
          <button
            id="addLeg"
            className="action-button"
            onClick={handleAddLeg}
            type="button"
          >
            Add Leg
          </button>
        </div>

        <div className="aircraft-options-container">
          <h3>Aircraft Options</h3>
          {state.aircraftOptions.map((option, index) => (
            <div
              className={`aircraft-options-block aircraft-options-block[${index}]`}
              key={index}
            >
              <div className="three-fields mb-20">
                <div className="form-group">
                  <label htmlFor={`aircraft[${index}]`}>Aircraft</label>
                  <AsyncSelect
                    name={`aircraft[${index}]`}
                    className="aircraft-select"
                    classNamePrefix="select"
                    cacheOptions
                    key={`aircraft[${index}]${option.aircraft.name}`}
                    loadOptions={loadAircraftOptions}
                    placeholder="Search Aircraft"
                    defaultValue={{
                      label: option.aircraft.name,
                      value: option.aircraft.id,
                    }}
                    onChange={(selectedAircraft) => {
                      dispatch({
                        type: "optionFieldChanged",
                        payload: {
                          optionIndex: index,
                          field: "aircraft",
                          value: {
                            name: selectedAircraft.name,
                            seats: selectedAircraft.seats,
                            yom: selectedAircraft.yom,
                            opi: selectedAircraft.opi || "",
                            id: selectedAircraft.id,
                          },
                        },
                      });
                    }}
                  ></AsyncSelect>
                </div>

                <TextInput
                  type={"number"}
                  name={`seats[${index}]`}
                  label="Seats"
                  handleChange={(e) => {
                    dispatch({
                      type: "optionAircraftSubFieldChanged",
                      payload: {
                        optionIndex: index,
                        field: "seats",
                        value: parseInt(e.target.value) || "",
                      },
                    });
                  }}
                  value={option.aircraft.seats}
                />

                <TextInput
                  type={"number"}
                  name={`opi[${index}]`}
                  label="OPI"
                  key={`opi[${index}]${option.aircraft.id}`}
                  handleChange={(e) => {
                    dispatch({
                      type: "optionAircraftSubFieldChanged",
                      payload: {
                        optionIndex: index,
                        field: "opi",
                        value: parseInt(e.target.value) || "",
                      },
                    });
                  }}
                  value={option.aircraft.opi || ""}
                />
              </div>

              <div className="three-fields mb20">
                <TextInput
                  type={"number"}
                  name={`totalCost[${index}]`}
                  label="Total Cost"
                  handleChange={(e) => {
                    dispatch({
                      type: "optionFieldChanged",
                      payload: {
                        optionIndex: index,
                        field: "totalCost",
                        value: parseInt(e.target.value) || "",
                      },
                    });
                  }}
                  value={option.totalCost}
                />

                <TextInput
                  type={"text"}
                  name={`interiorRefurbYear[${index}]`}
                  label="Interior Refurb Year"
                  key={`interiorRefurbYear[${index}]${option.aircraft.id}`}
                  handleChange={(e) => {
                    dispatch({
                      type: "optionFieldChanged",
                      payload: {
                        optionIndex: index,
                        field: "interiorRefurbYear",
                        value: e.target.value || "",
                      },
                    });
                  }}
                  value={option.interiorRefurbYear || ""}
                />

                <TextInput
                  name={`yom[${index}]`}
                  label="YOM"
                  handleChange={(e) => {
                    dispatch({
                      type: "optionAircraftSubFieldChanged",
                      payload: {
                        optionIndex: index,
                        field: "yom",
                        value: e.target.value || "",
                      },
                    });
                  }}
                  value={option.aircraft.yom}
                />
              </div>

              <TextAreaInput
                name={`notes[${index}]`}
                label="Additional Notes"
                handleChange={(e) => {
                  dispatch({
                    type: "optionFieldChanged",
                    payload: {
                      optionIndex: index,
                      field: "notes",
                      value: e.target.value,
                    },
                  });
                }}
                value={option.notes}
                containerClasses={"mb-20"}
              ></TextAreaInput>

              <div className="leg-duration-block">
                {state.legs.map((leg, legIndex) => {
                  return (
                    <Fragment key={`legFields[${legIndex}]`}>
                      <TextInput
                        key={`legDuration[${legIndex}]`}
                        name={`legDuration[${legIndex}]`}
                        label={`Leg ${legIndex + 1} Duration`}
                        handleChange={(e) => {
                          dispatch({
                            type: "optionArrayFieldChanged",
                            payload: {
                              optionIndex: index,
                              legIndex: legIndex,
                              value: e.target.value,
                              arrayField: "legDuration",
                            },
                          });
                        }}
                        value={option.legDurations[legIndex] || ""}
                      />
                      <TextInput
                        key={`fuelStop[${legIndex}]`}
                        name={`fuelStop[${legIndex}]`}
                        label={`Fuel Stops - Leg ${legIndex + 1}`}
                        handleChange={(e) => {
                          dispatch({
                            type: "optionArrayFieldChanged",
                            payload: {
                              optionIndex: index,
                              legIndex: legIndex,
                              value: parseInt(e.target.value) || "",
                              arrayField: "fuelStop",
                            },
                          });
                        }}
                        value={option.fuelStops[legIndex] || ""}
                      />
                    </Fragment>
                  );
                })}
              </div>

              {state.aircraftOptions[index].aircraft.id &&
                state.aircraftOptions[index].aircraft.name && (
                  <button
                    className="evo-blue base-button mb-20"
                    onClick={(e) => {
                      e.preventDefault();
                      handleFlightTimeCalculation(index);
                    }}
                  >
                    Calculate Flight Times for{" "}
                    {state.aircraftOptions[index].aircraft.name}
                  </button>
                )}

              <div className="aircraft-checkboxes">
                <div className="safety-standards-container checkbox-list">
                  <h4>Safety Rating</h4>
                  {safetyStandards.map(({ label: standard }) => {
                    return (
                      <div key={`${standard}[${index}]-key`}>
                        <input
                          type="checkbox"
                          name={`${standard}[${index}]`}
                          id={`${standard}[${index}]`}
                          onChange={(e) => {
                            dispatch({
                              type: "optionCheckboxFieldChanged",
                              payload: {
                                optionIndex: index,
                                field: "safetyStandards",
                                name: standard,
                                value: e.target.checked ? true : false,
                              },
                            });
                          }}
                          checked={
                            option?.safetyStandards?.includes(standard)
                              ? "checked"
                              : false
                          }
                        ></input>
                        <label htmlFor={`${standard}[${index}]`}>
                          {standard}
                        </label>
                      </div>
                    );
                  })}
                </div>

                <div className="amenities-container checkbox-list">
                  <h4>Amenities</h4>

                  {amenities.map(({ label: amenity }) => {
                    return (
                      <div key={`${amenity}[${index}]-key`}>
                        <input
                          type="checkbox"
                          name={amenity[index]}
                          id={`${amenity}[${index}]`}
                          onChange={(e) => {
                            dispatch({
                              type: "optionCheckboxFieldChanged",
                              payload: {
                                optionIndex: index,
                                field: "amenities",
                                name: amenity,
                                value: e.target.checked ? true : false,
                              },
                            });
                          }}
                          checked={
                            option?.amenities?.includes(amenity)
                              ? "checked"
                              : false
                          }
                        ></input>
                        <label htmlFor={`${amenity}[${index}]`}>
                          {amenity}
                        </label>
                      </div>
                    );
                  })}
                </div>

                <div className="important-notes-container checkbox-list">
                  <h4>Important Notes</h4>

                  <div key={`Owner approval[${index}]-key`}>
                    <input
                      type="checkbox"
                      name={`Owner approval[${index}]`}
                      id={`Owner approval[${index}]`}
                      onChange={(e) => {
                        // Update importantNotesList
                        dispatch({
                          type: "optionCheckboxFieldChanged",
                          payload: {
                            optionIndex: index,
                            field: "importantNotesList",
                            name: "Owner approval",
                            value: e.target.checked ? true : false,
                          },
                        });

                        // Also update the ownerApprovalRequired field for backward compatibility
                        dispatch({
                          type: "optionFieldChanged",
                          payload: {
                            optionIndex: index,
                            field: "ownerApprovalRequired",
                            value: e.target.checked,
                          },
                        });
                      }}
                      checked={
                        option?.importantNotesList?.includes(
                          "Owner approval",
                        ) || option.ownerApprovalRequired
                          ? "checked"
                          : false
                      }
                    ></input>
                    <label htmlFor={`Owner approval[${index}]`}>
                      Owner approval
                    </label>
                  </div>

                  {importantNotes.map(({ label: note }) => {
                    return (
                      <div key={`${note}[${index}]-key`}>
                        <input
                          type="checkbox"
                          name={`${note}[${index}]`}
                          id={`${note}[${index}]`}
                          onChange={(e) => {
                            dispatch({
                              type: "optionCheckboxFieldChanged",
                              payload: {
                                optionIndex: index,
                                field: "importantNotesList",
                                name: note,
                                value: e.target.checked ? true : false,
                              },
                            });
                          }}
                          checked={
                            option?.importantNotesList?.includes(note)
                              ? "checked"
                              : false
                          }
                        ></input>
                        <label htmlFor={`${note}[${index}]`}>{note}</label>
                      </div>
                    );
                  })}
                </div>
              </div>

              <button
                className="deleteOption action-button"
                onClick={(e) => {
                  e.preventDefault();
                  return dispatch({ type: "optionDeleted", index: index });
                }}
                type="button"
              >
                Delete Aircraft Option
              </button>
            </div>
          ))}

          <button
            id="addOption"
            className="action-button"
            type="button"
            onClick={handleAddOption}
          >
            Add Aircraft Option
          </button>
        </div>

        <Editor
          editorState={state.footerEditor}
          wrapperClassName="header-editor-wrapper"
          editorClassName="header-editor"
          onEditorStateChange={(editorState) => {
              setUnsavedChanges({
                field: 'footerEditor',
                value:
                  editorState?.getCurrentContent().getPlainText()
                  !== state.initialFormValues.footerEditor?.getCurrentContent().getPlainText(),
              });

              dispatch({
                type: "fieldChanged",
                field: "footerEditor",
                value: editorState,
              })
            }
          }
        />

        <LoadingButton
          className="base-button evo-blue w-100"
          type="submit"
          isLoading={state.isFormLoading}
        >
          Save and Preview
        </LoadingButton>
      </form>

      <UnsavedChangesWarningModal isChanged={!!Object.keys(state.unsavedChanges).length}/>
    </Fragment>
  );
};

export default DuplicateQuoteContent;
